import { ICommandBarItemProps } from '@fluentui/react';
import AppContext from 'App/AppContext';
import { IPCommandBar, IPSpinner, IPText } from 'baseComponents';
import { IPSpinnerSize } from 'baseComponents/Spinner/IPSpinner';
import { IPStack } from 'baseComponents/Stack/IPStack';
import { getOfficeOnlineViewerUrl, needsOfficeOnlineViewer } from 'components/Links/Helpers/officeOnlineViewer';
import MyListItems from 'components/SharePoint/MyListItems';
import SharePointPageRenderer from 'components/SharePoint/PageRenderer/SharePointPageRenderer';
import 'components/SharePoint/PageRenderer/SharePointPageRenderer.css';
import { cancelIcon, globalStackTokensGapMedium } from 'globalStyles';
import { ColumnDefinition } from 'microsoft-graph';
import { PublishingStateSharePoint, ResourceLinkVersion } from 'models/resourceLink';
import { ResourceListType } from 'models/resourceList';
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiRequest } from 'services/Auth/authConfig';
import { IListItem, ISitePage } from 'services/Graph/SharepointInterfaces';
import logger from 'services/Logging/logService';
import {
  GetContentFileInfo,
  GetContentListColumnDef,
  GetContentListItem,
  GetContentListItems,
  GetContentPage,
} from 'services/Wrappers/contentService';
import { getLocalStorageData, LocalStorageKeys, setLocalStorageData } from 'utils/localstorage';
import { PageHeaderDoc } from '../../SharePoint/PageRenderer/SharePointPageHeaders';
import { ILinkPreviewProps } from './LinkPreview';
import { LinkPreviewConfirmation } from './LinkPreviewConfirmation';
import { PreviewIFrame } from './PreviewIFrame';

export const LinkPreviewVirtual = (props: ILinkPreviewProps) => {
  const appContext = useContext(AppContext);
  const { t } = useTranslation(['library']);
  //Starts true for the same reason as in LinkPreviewSharePoint: getData runs from an effect, so a false start
  //makes the first committed render show the "not available" text before loading has even begun.
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [webUrl, setWebUrl] = useState<string | undefined>(undefined);
  const [page, setPage] = useState<ISitePage | undefined>(undefined);
  const [columns, setColumns] = useState<ColumnDefinition[]>([]);
  const [items, setItems] = useState<IListItem[]>([]);
  const [fitToWidth, setFitToWidth] = useState<boolean>(true);

  useEffect(() => {
    const fit = getLocalStorageData(appContext, LocalStorageKeys.PageContentFitToWidth);
    if (fit === 'false') {
      setFitToWidth(false);
    } else {
      setFitToWidth(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (props.link) {
      getData();
    } else {
      //Nothing to load, so nothing will clear the initial loading state.
      setIsLoading(false);
      clear();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.link]);

  const getCommandBarItems = (): ICommandBarItemProps[] => {
    const items: ICommandBarItemProps[] = [];

    //const libraryOpenInModal = appContext.globalDataCache.settings.get(LibraryOpenInModal) as boolean;
    //if (libraryOpenInModal) return [];

    if (props.onClose) {
      items.push({
        key: 'close',
        disabled: false,
        text: t('library:PinnedLinkTabs.Commands.Close'),
        iconProps: cancelIcon,
        onClick: () => {
          if (props.onClose) props.onClose();
        },
      });
    }

    if (props.extraCommandBarButtons) {
      items.push(...props.extraCommandBarButtons);
    }

    return items;
  };

  const getCommandBarOverflowItems = (): ICommandBarItemProps[] => {
    const items: ICommandBarItemProps[] = [];

    //const libraryOpenInModal = appContext.globalDataCache.settings.get(LibraryOpenInModal) as boolean;
    //if (libraryOpenInModal) return [];

    if (page) {
      //show the fit-to-width button only if the page is available
      items.push({
        key: 'fitToWidth',
        disabled: false,
        checked: fitToWidth,
        canCheck: true,
        text: t('library:PinnedLinkTabs.Commands.FitToWidth'),
        iconProps: { iconName: 'FitWidth' },
        onClick: () => {
          const newFitToWidth = !fitToWidth;
          setFitToWidth(newFitToWidth);
          setLocalStorageData(appContext, LocalStorageKeys.PageContentFitToWidth, newFitToWidth.toString());
        },
      });
    }

    return items;
  };

  const clear = () => {
    setPage(undefined);
  };

  const getData = async () => {
    try {
      if (!props.link) return;
      setIsLoading(true);
      clear();

      logger.debug('Getting link preview data for ' + props.link.linkName);

      if (props.link.list.listType === ResourceListType.SitePageLibrary) {
        const accessToken: string = await appContext.getAccessToken(apiRequest.scopes);
        const page = await GetContentPage(props.link, accessToken);
        setPage(page);
      } else if (props.link.list.listType === ResourceListType.DocumentLibrary) {
        const accessToken: string = await appContext.getAccessToken(apiRequest.scopes);
        const fileInfo = await GetContentFileInfo(props.link, accessToken);
        if (fileInfo) {
          if (needsOfficeOnlineViewer(fileInfo.mimeType)) {
            setWebUrl(getOfficeOnlineViewerUrl(fileInfo.url));
          } else {
            //These files are served from the back-end in an iframe
            //The X-Frame-Options header must be set to SAMEORIGIN
            setWebUrl(fileInfo.url);
          }
        }
      } else if (props.link.list.listType === ResourceListType.CustomList) {
        const accessToken: string = await appContext.getAccessToken(apiRequest.scopes);
        const columns = await GetContentListColumnDef(props.link.list, accessToken);
        setColumns(columns);
        if (props.link.isWholeList()) {
          const items = await GetContentListItems(props.link.list, accessToken);
          setItems([...items]);
        } else {
          const item = await GetContentListItem(props.link, accessToken);
          setItems([item]);
        }
      } else if (props.link.list.listType === ResourceListType.WebURL) {
        const webUrl: string = props.link.list.webURL || '';
        if (webUrl) setWebUrl(webUrl);
      }

      //create dummy version info let the parent know that the version info is loaded
      const versionInfo = new ResourceLinkVersion();
      versionInfo.version = '1.0';
      versionInfo.date = new Date();
      versionInfo.publishingState = PublishingStateSharePoint.Published;

      props.onVersionLoaded?.(versionInfo);
    } catch (err) {
      appContext.setError(err);
    } finally {
      setIsLoading(false);
    }
  };

  const getCommandBar = () => {
    if (props.hideCommandbar) return null;
    const items = getCommandBarItems();
    const overflowItems = getCommandBarOverflowItems();
    if (items.length === 0 && overflowItems.length === 0) return null;

    return (
      <IPStack.Item>
        <IPCommandBar items={items} overflowItems={overflowItems} />
      </IPStack.Item>
    );
  };

  //
  // Main render
  //
  if (!props.link) return null;

  //
  // For links of type 'whole list' render a file picker or list item picker in view-mode
  //
  if (props.link.isWholeList()) {
    switch (props.link.list.listType) {
      case ResourceListType.CustomList:
        return (
          <IPStack verticalFill tokens={globalStackTokensGapMedium}>
            {getCommandBar()}
            <MyListItems
              items={items}
              columns={columns}
              loading={isLoading}
              onSelect={(_items: IListItem[]) => {}}
              viewMode={true}
              existingLinks={[]}
              linkToEdit={props.link}
            />
          </IPStack>
        );
      default:
        //cannot render preview for other types
        return null;
    }
  }

  //
  // For custom list items, render the list with item filter
  //
  if (props.link.list.listType === ResourceListType.CustomList) {
    return (
      <IPStack verticalFill tokens={globalStackTokensGapMedium}>
        {getCommandBar()}
        <MyListItems
          items={items}
          columns={columns}
          loading={isLoading}
          onSelect={(_items: IListItem[]) => {}}
          viewMode={true}
          existingLinks={[]}
          linkToEdit={props.link}
        />
      </IPStack>
    );
  }

  //
  // For other individual items, render the Embed-url in an iframe
  //
  return (
    <IPStack verticalFill tokens={globalStackTokensGapMedium}>
      {getCommandBar()}
      {isLoading && (
        <IPStack verticalFill horizontalAlign="center" verticalAlign="center">
          <IPSpinner size={IPSpinnerSize.large} />
        </IPStack>
      )}
      {!isLoading && !page && !webUrl && (
        <IPStack verticalFill horizontalAlign="center" verticalAlign="center">
          <IPText>{props.unavailableText ?? t('translation:General.Notifications.NotAvailable')}</IPText>
        </IPStack>
      )}
      {!isLoading && page && (
        <SharePointPageRenderer
          link={props.link}
          page={page}
          owner={props.link}
          previewLinks={true}
          fitToWidth={fitToWidth}
          hideHeader={props.hideHeader}
          siteId={props.link.list.spSiteId}
        />
      )}
      {!isLoading && !page && webUrl && (
        <IPStack verticalFill>
          {props.link.list.listType === ResourceListType.DocumentLibrary && !props.hideHeader && (
            <PageHeaderDoc link={props.link} activity={undefined} />
          )}
          <IPStack.Item grow>
            <PreviewIFrame title={props.link.linkName} src={webUrl} webUrl={webUrl} />
          </IPStack.Item>
        </IPStack>
      )}
      {props.link?.hasRC() && props.onConfirmed && props.onNext && (
        <IPStack.Item>
          <LinkPreviewConfirmation
            link={props.link}
            onConfirmed={props.onConfirmed}
            onNext={props.onNext}
            version={'1.0'} //links in virtual lists do not have version info
            hasNext={true}
          />
        </IPStack.Item>
      )}
    </IPStack>
  );
};
